import axios from 'axios';
import {get, post} from './http';
import {type FlagColor} from './flags';

export type RiskLevel = 'high' | 'medium' | 'low';

// One node of a vertical's classification taxonomy. `concept_id` is the taxonomy concept the node stands for
// (what tasks are matched and claimed by; null for the two buckets and for a node with no concept). `available`
// is the node plus its descendants, `own` only tasks suggested exactly as this node. The top level ends with the
// "Unclassified", "Not in taxonomy" and "External Referrals" buckets when they hold work; the last has one child
// per referring application, whose `concept_id` is its filter value (`referral:<source>`).
export type ClassificationTreeNode = {
  name: string;
  concept_id: string | null;
  available: number;
  own: number;
  children: ClassificationTreeNode[];
};

// Work available in one item language (base code, null when the item declares none).
export type LanguageQueueSummary = {language: string | null; available: number};

// Values the claim filters use for "no suggested risk", "a suggestion no taxonomy node carries", "referred by
// another application" and "no declared language", and the names the tree gives the three buckets.
export const UNCLASSIFIED = 'unclassified';
export const NOT_IN_TAXONOMY = 'not_in_taxonomy';
export const REFERRAL = 'referral';
export const UNCLASSIFIED_NAME = 'Unclassified';
export const NOT_IN_TAXONOMY_NAME = 'Not in taxonomy';
export const REFERRAL_NAME = 'External Referrals';
export const UNKNOWN_LANGUAGE = 'unknown';

export type PolicyViolationQueueSummary = {
  policy_violation: string;
  // Tasks that can be served right now.
  available: number;
  video: number;
  channel: number;
  // How much of the available work the engine rated high risk.
  high_risk: number;
  tree: ClassificationTreeNode[];
};

// How the queue is offered to the signed-in analyst.
export type ModerationQueueSettings = {
  channel_review_enabled: boolean;
  // Reviewed only as channels: never offered in the video queue.
  channel_only_policy_violations: string[];
};

export type ModerationQueueKind = 'video' | 'channel';

// What narrows a claim beyond the policy violation. A classification is a taxonomy concept id (its whole
// subtree unless the scope is "own"), UNCLASSIFIED, NOT_IN_TAXONOMY, REFERRAL or `referral:<source>`; a language of UNKNOWN_LANGUAGE
// matches items that declare none.
export type QueueClaimFilters = {
  classification?: string;
  classificationScope?: ClassificationScope;
  language?: string;
};

export type QueueClaimSelection = {
  policyViolation: string;
  classification?: string;
  classificationScope?: ClassificationScope;
};

export type QueueClaim = {
  policyViolation?: string;
  selections?: readonly QueueClaimSelection[];
  size?: number;
  kind?: ModerationQueueKind;
  filters?: QueueClaimFilters;
  restart?: boolean;
};

export type ClassificationScope = 'subtree' | 'own';

export type ChannelVideo = {
  id: string;
  external_id: string;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  published_at: string | null;
  risk_level: RiskLevel | null;
  // State of its task in this policy violation, if it has one.
  task_state: string | null;
  flag: FlagColor | null;
  is_strike: boolean;
  // Assessed by the risk engine; assessed without a strike means non-violative.
  is_assessed: boolean;
  strike_risk_type: string | null;
};

// What another analyst already decided for the same item under a different policy violation, as of
// the moment this task was served.
export type OtherVerticalReview = {
  policy_violation: string;
  risks: string[];
  analyst_email: string;
  created_at: string;
};

export type ModerationTask = {
  id: string;
  item_id: string;
  job_id: string | null;
  policy_violation: string;
  risk_level: RiskLevel;
  confidence: number;
  rationale: string;
  // The engine's finding by name, for display; suggested_concept_ids is what it is matched by (first is primary).
  suggested_risk: string | null;
  suggested_concept_ids: string[];
  model: string;
  // Proof of ownership: every release, dismiss, reclassify and classification must present it.
  lease_id: string;
  created_at: string;
  external_id: string;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  author_handle: string | null;
  author_name: string | null;
  author_id: string | null;
  published_at: string | null;
  metadata: Record<string, unknown>;
  platform: string;
  affiliate: string;
  content_type: string;
  // The reviewer's own flag on the queued item - the same one Item.flag carries.
  flag: FlagColor | null;
  // Opened by a reclassification: it cannot be reclassified again.
  is_reclassified: boolean;
  // Why the analyst who sent it here judged the engine wrong; set when is_reclassified.
  reclassification_note: string | null;
  // The application a person referred the item from (e.g. "nile-ssh"), and their reason. Kept when risk
  // detection also flags the item.
  referral_source: string | null;
  referral_reason: string | null;
  // Queued by a referral alone: there is no engine rationale or confidence to show.
  referral_only: boolean;
  channel_videos: ChannelVideo[];
  other_verticals: OtherVerticalReview[];
};

// Why an analyst could not judge an item; the server refuses anything else.
export type IndeterminateReason = 'removed' | 'broken' | 'not_reviewable' | 'unsure';

export const INDETERMINATE_REASONS: ReadonlyArray<{value: IndeterminateReason; label: string}> = [
  {value: 'removed', label: 'Removed or unavailable'},
  {value: 'broken', label: 'Broken (won’t load or play)'},
  {value: 'not_reviewable', label: 'Can’t be reviewed'},
  {value: 'unsure', label: 'Unsure'}
];

// Analytics only - never gates a decision. Sent with every decision (classify, dismiss, reclassify).
export type ReviewSignals = {
  // The analyst clicked through to the original or expanded the media.
  opened?: boolean;
  // Omitted unless the page can measure it (the embedded player gives no playback signal).
  played_seconds?: number;
  // Seconds this task was the active one while the document was visible.
  on_screen_seconds?: number;
  // Shared by every decision one bulk action produces; omitted for single decisions.
  bulk_group_id?: string;
};

// What an analyst holds: one lease over one task or a batch, in serving order. One heartbeat keeps
// all of it.
export type ModerationLease = {
  lease_id: string;
  lease_expires_at: string;
  heartbeat_seconds: number;
  requested_size: number;
  tasks: ModerationTask[];
};

export type LeaseRenewed = {
  lease_expires_at: string;
  // Tasks of the lease that someone else classified since they were served.
  withdrawn: ResolvedElsewhere[];
};

export type TaskLease = {id: string; leaseId: string; review?: ReviewSignals};

// Someone else classified the item while this analyst held its task, so the task was completed.
export type ResolvedElsewhere = {task_id: string; by_name: string; at: string};

// What a write on such a task answers: 409 with this code, who classified it and when.
export const RESOLVED_ELSEWHERE_CODE = 'task_resolved_elsewhere';

export const resolvedElsewhereOf = (error: unknown): ResolvedElsewhere | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }

  const data = (error.response.data ?? {}) as Partial<ResolvedElsewhere> & {code?: string};
  return data.code === RESOLVED_ELSEWHERE_CODE && data.task_id && data.by_name
    ? {
        task_id: data.task_id,
        by_name: data.by_name,
        at: data.at ?? ''
      }
    : null;
};

// 409 from heartbeat, release, dismiss, reclassify or classify: the lease expired or was
// reassigned. A task someone else classified is a 409 too, but a different one; see above.
export const isLeaseLost = (error: unknown) =>
  axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  resolvedElsewhereOf(error) === null;

export const moderationQueueApi = {
  // Language and kind narrow every count; omit for all.
  policyViolations: async (language?: string, kind?: ModerationQueueKind) =>
    get<PolicyViolationQueueSummary[]>('/moderation-queue/policy-violations', {language, kind}),

  settings: async () => get<ModerationQueueSettings>('/moderation-queue/settings'),

  // Available work per item language, within one policy violation when given.
  languages: async (policyViolation?: string, kind?: ModerationQueueKind) =>
    get<LanguageQueueSummary[]>('/moderation-queue/languages', {
      policy_violation: policyViolation,
      kind
    }),

  // Returns the analyst's held lease if they have one (whatever size/policy was asked for), else
  // claims a new one; null when nothing is available (204). `restart` skips whatever is held back to
  // the pool first, so starting a queue never serves a task left behind earlier.
  async next({
    policyViolation,
    selections,
    size = 1,
    kind = 'video',
    filters = {},
    restart = false
  }: QueueClaim): Promise<ModerationLease | null> {
    const data = await post<
      {
        policy_violation?: string;
        selections?: Array<{
          policy_violation: string;
          classification?: string;
          classification_scope?: ClassificationScope;
        }>;
        size: number;
        kind: ModerationQueueKind;
        classification?: string;
        classification_scope?: ClassificationScope;
        language?: string;
        restart?: boolean;
      },
      ModerationLease | ''
    >('/moderation-queue/next', {
      ...(selections
        ? {
            selections: selections.map((selection) => ({
              policy_violation: selection.policyViolation,
              ...(selection.classification
                ? {
                    classification: selection.classification,
                    classification_scope: selection.classificationScope ?? 'subtree'
                  }
                : {})
            }))
          }
        : {policy_violation: policyViolation!}),
      size,
      kind,
      ...(selections
        ? {}
        : filters.classification
          ? {
              classification: filters.classification,
              classification_scope: filters.classificationScope ?? 'subtree'
            }
          : {}),
      ...(filters.language ? {language: filters.language} : {}),
      ...(restart ? {restart} : {})
    });
    return data === '' ? null : data;
  },

  // One call keeps every task of the lease.
  heartbeat: async (leaseId: string) =>
    post<{lease_id: string}, LeaseRenewed>('/moderation-queue/heartbeat', {
      lease_id: leaseId
    }),

  // Skip: back to the pool, withheld from this analyst for a while. The rest of the batch stays
  // leased.
  release: async (id: string, leaseId: string) =>
    post<{lease_id: string}>(`/moderation-queue/${id}/release`, {lease_id: leaseId}),

  // Not violative: a final decision for this policy violation, no classification.
  dismiss: async (id: string, leaseId: string, review?: ReviewSignals) =>
    post<{lease_id: string; review?: ReviewSignals}>(`/moderation-queue/${id}/dismiss`, {
      lease_id: leaseId,
      ...(review ? {review} : {})
    }),

  // Can't review: the analyst could not judge the item, so the risk is unknown rather than absent. A
  // final decision for this policy violation, no classification.
  indeterminate: async (
    id: string,
    leaseId: string,
    reason: IndeterminateReason,
    review?: ReviewSignals
  ) =>
    post<{lease_id: string; reason: IndeterminateReason; review?: ReviewSignals}>(
      `/moderation-queue/${id}/indeterminate`,
      {
        lease_id: leaseId,
        reason,
        ...(review ? {review} : {})
      }
    )
};
